/**
 * Divisória arrastável entre dois painéis.
 *
 * O painel é uma ferramenta de operação: quem passa o turno olhando a fila
 * quer a timeline larga, quem está ajustando perfis de Anti-Ban quer a coluna
 * da direita. Uma proporção fixa serve mal aos dois.
 *
 * Três coisas que a implementação ingênua erra:
 *
 * * **Área de clique.** A linha tem 6 px, mas acertá-la com o mouse a 6 px é
 *   irritante. O elemento é largo o bastante para pegar (11 px) e só a faixa
 *   central é pintada.
 * * **Seleção de texto no arrasto.** Sem bloquear, arrastar sobre a tabela vai
 *   selecionando tudo por onde passa. `setPointerCapture` mantém os eventos
 *   no divisor e a classe no `body` mata a seleção.
 * * **Teclado.** É um `separator` de verdade: setas movem, Home reseta. Quem
 *   opera o painel por teclado não fica sem a funcionalidade.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const PREFIXO = 'sga.divisor.';

function ler(chave: string, inicial: number): number {
  try {
    const bruto = window.localStorage.getItem(PREFIXO + chave);
    if (bruto === null) return inicial;
    const v = Number(bruto);
    return Number.isFinite(v) ? v : inicial;
  } catch {
    // Webview com storage bloqueado: usa o padrão e segue.
    return inicial;
  }
}

function gravar(chave: string, valor: number) {
  try {
    window.localStorage.setItem(PREFIXO + chave, String(valor));
  } catch {
    // Sem persistência, a divisória volta ao padrão no próximo acesso — o
    // painel continua funcionando.
  }
}

export interface OpcoesDivisor {
  /** Identidade da divisória no `localStorage`. Uma por tela. */
  chave: string;
  inicial: number;
  min: number;
  max: number;
  /** Converte a posição do ponteiro no valor guardado. */
  calcular: (clientX: number, area: DOMRect) => number;
  /** Passo do teclado, na unidade do valor. */
  passo?: number;
  rotulo: string;
}

export interface Divisor {
  valor: number;
  areaRef: React.RefObject<HTMLDivElement | null>;
  props: DivisorProps;
}

interface DivisorProps {
  rotulo: string;
  valor: number;
  min: number;
  max: number;
  aoIniciar: (e: React.PointerEvent<HTMLDivElement>) => void;
  aoTeclar: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  aoResetar: () => void;
}

export function useDivisor(o: OpcoesDivisor): Divisor {
  const { chave, inicial, min, max, calcular, passo = 16, rotulo } = o;
  const areaRef = useRef<HTMLDivElement>(null);
  const [valor, setValor] = useState(() => ler(chave, inicial));

  // O valor entra no `localStorage` com atraso: arrastar dispara dezenas de
  // atualizações por segundo, e gravar em todas trava a UI em webview.
  useEffect(() => {
    const t = window.setTimeout(() => gravar(chave, valor), 250);
    return () => window.clearTimeout(t);
  }, [chave, valor]);

  const limitar = useCallback((v: number) => Math.min(max, Math.max(min, v)), [min, max]);

  const aoIniciar = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const area = areaRef.current?.getBoundingClientRect();
      if (!area) return;

      e.preventDefault();
      const alvo = e.currentTarget;
      alvo.setPointerCapture(e.pointerId);
      document.body.classList.add('sga-arrastando');

      const mover = (ev: PointerEvent) => setValor(limitar(calcular(ev.clientX, area)));
      const soltar = () => {
        alvo.releasePointerCapture(e.pointerId);
        document.body.classList.remove('sga-arrastando');
        alvo.removeEventListener('pointermove', mover);
        alvo.removeEventListener('pointerup', soltar);
        alvo.removeEventListener('pointercancel', soltar);
      };

      alvo.addEventListener('pointermove', mover);
      alvo.addEventListener('pointerup', soltar);
      // `pointercancel` cobre o caso de o sistema tomar o gesto (rolagem por
      // toque, troca de janela). Sem ele, a classe do `body` ficava presa e a
      // seleção de texto morria no painel inteiro.
      alvo.addEventListener('pointercancel', soltar);
    },
    [calcular, limitar],
  );

  const aoTeclar = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const salto = e.shiftKey ? passo * 3 : passo;
      if (e.key === 'ArrowLeft') setValor((v) => limitar(v - salto));
      else if (e.key === 'ArrowRight') setValor((v) => limitar(v + salto));
      else if (e.key === 'Home' || e.key === 'Enter') setValor(inicial);
      else return;
      e.preventDefault();
    },
    [passo, limitar, inicial],
  );

  const aoResetar = useCallback(() => setValor(inicial), [inicial]);

  return {
    valor,
    areaRef,
    props: { rotulo, valor, min, max, aoIniciar, aoTeclar, aoResetar },
  };
}

/** A alça em si. Vai entre os dois painéis. */
export function Divisor({ rotulo, valor, min, max, aoIniciar, aoTeclar, aoResetar }: DivisorProps) {
  return (
    <div
      className="sga-divisor"
      role="separator"
      aria-orientation="vertical"
      aria-label={rotulo}
      aria-valuenow={Math.round(valor)}
      aria-valuemin={Math.round(min)}
      aria-valuemax={Math.round(max)}
      tabIndex={0}
      title={`${rotulo} — arraste, ou use as setas. Duplo clique volta ao padrão.`}
      onPointerDown={aoIniciar}
      onKeyDown={aoTeclar}
      onDoubleClick={aoResetar}
    >
      <span className="sga-divisor__alca" />
    </div>
  );
}
